import { httpClient } from '../http-client.service';
import { ApiResponse } from '../../types/responses/api-response';
import {
  IAnomalyDetection,
  IAnomalySettings,
  IAnomalyAlert,
  AnomalyAlgorithm,
  ICorrelationData,
  GetAnomalyAlertsResponse,
  AcknowledgeAllAlertsRequest,
  AcknowledgeAllAlertsResponse,
  ResolveAllAlertsRequest,
  ResolveAllAlertsResponse,
} from '../../types/analytics';
import { ANALYTICS_ANOMALIES } from '../../constants/api-endpoints';

const BASE_URL = ANALYTICS_ANOMALIES;

/** Точка временного ряда аномалий (совместима с IAnomalyPoint в виджете) */
export interface IAnomalyPoint {
  timestamp: string;
  value: number;
  predicted: number;
  anomaly: boolean;
  severity: 'low' | 'medium' | 'high' | 'critical';
  confidence: number;
}

export interface GetAnomalyHistoryParams {
  widgetId: string;
  from?: string;
  to?: string;
  limit?: number;
}

/**
 * GET /analytics/anomalies/history — история точек аномалий по виджету (идемпотентное чтение).
 * Ответ: { data: IAnomalyPoint[] } или IAnomalyPoint[].
 */
export const getAnomalyHistory = async (
  params: GetAnomalyHistoryParams
): Promise<IAnomalyPoint[]> => {
  const { data } = await httpClient.get<ApiResponse<IAnomalyPoint[]> | IAnomalyPoint[]>(
    `${BASE_URL}/history`,
    { params }
  );
  const raw = (data as ApiResponse<IAnomalyPoint[]>)?.data ?? (data as IAnomalyPoint[]);
  return Array.isArray(raw) ? raw : [];
};

export interface DetectAnomaliesRequest {
  widgetId: string;
  dataSource: string;
  metric: string;
  settings: IAnomalySettings;
}

export const detectAnomalies = async (
  payload: DetectAnomaliesRequest
): Promise<IAnomalyDetection> => {
  const { data } = await httpClient.post<ApiResponse<IAnomalyDetection>>(
    `${BASE_URL}/detect`,
    payload
  );

  return data.data;
};

export interface TrainAnomalyModelRequest {
  widgetId: string;
  dataSource: string;
  metric: string;
  algorithm: AnomalyAlgorithm;
  parameters?: Record<string, any>;
}

export interface TrainAnomalyModelResponse {
  widgetId: string;
  algorithm: AnomalyAlgorithm;
  accuracy: number;
  trainedAt: string;
}

export const trainAnomalyModel = async (
  payload: TrainAnomalyModelRequest
): Promise<TrainAnomalyModelResponse> => {
  const { data } = await httpClient.post<ApiResponse<TrainAnomalyModelResponse>>(
    `${BASE_URL}/train`,
    payload
  );

  return data.data;
};

export interface UpdateAnomalySettingsRequest {
  widgetId: string;
  settings: IAnomalySettings;
}

export const updateAnomalySettings = async (
  payload: UpdateAnomalySettingsRequest
): Promise<IAnomalyDetection> => {
  const { data } = await httpClient.put<ApiResponse<IAnomalyDetection>>(
    `${BASE_URL}/${payload.widgetId}/settings`,
    payload.settings
  );

  return data.data;
};

export interface GetAnomalyAlertsParams {
  widgetId?: string;
  limit?: number;
  severity?: 'low' | 'medium' | 'high' | 'critical';
  acknowledged?: boolean;
  resolved?: boolean;
  page?: number;
  pageSize?: number;
}

export const getAnomalyAlerts = async (
  params: GetAnomalyAlertsParams = {}
): Promise<IAnomalyAlert[]> => {
  const { data } = await httpClient.get<ApiResponse<GetAnomalyAlertsResponse | IAnomalyAlert[]>>(
    `${BASE_URL}/alerts`,
    { params }
  );

  // Обработка пагинации: если бэкенд возвращает объект с пагинацией, извлекаем items
  if (Array.isArray(data.data)) {
    return data.data;
  } else {
    return (data.data as GetAnomalyAlertsResponse).items;
  }
};

// Управление алертами
export interface AcknowledgeAnomalyAlertRequest {
  note?: string;
}

export const acknowledgeAnomalyAlert = async (
  alertId: string,
  payload?: AcknowledgeAnomalyAlertRequest
): Promise<IAnomalyAlert> => {
  const { data } = await httpClient.put<ApiResponse<IAnomalyAlert>>(
    `${BASE_URL}/alerts/${alertId}/acknowledge`,
    payload || {}
  );

  return data.data;
};

export interface ResolveAnomalyAlertRequest {
  resolutionNote?: string;
}

export const resolveAnomalyAlert = async (
  alertId: string,
  payload?: ResolveAnomalyAlertRequest
): Promise<IAnomalyAlert> => {
  const { data } = await httpClient.put<ApiResponse<IAnomalyAlert>>(
    `${BASE_URL}/alerts/${alertId}/resolve`,
    payload || {}
  );

  return data.data;
};

export interface DismissAnomalyAlertRequest {
  reason?: string;
}

export const dismissAnomalyAlert = async (
  alertId: string,
  payload?: DismissAnomalyAlertRequest
): Promise<IAnomalyAlert> => {
  const { data } = await httpClient.put<ApiResponse<IAnomalyAlert>>(
    `${BASE_URL}/alerts/${alertId}/dismiss`,
    payload || {}
  );

  return data.data;
};

// Массовые операции
export const acknowledgeAllAlerts = async (
  payload: AcknowledgeAllAlertsRequest
): Promise<AcknowledgeAllAlertsResponse> => {
  const { data } = await httpClient.post<ApiResponse<AcknowledgeAllAlertsResponse>>(
    `${BASE_URL}/alerts/acknowledge-all`,
    payload
  );

  return data.data;
};

export const resolveAllAlerts = async (
  payload: ResolveAllAlertsRequest
): Promise<ResolveAllAlertsResponse> => {
  const { data } = await httpClient.post<ApiResponse<ResolveAllAlertsResponse>>(
    `${BASE_URL}/alerts/resolve-all`,
    payload
  );

  return data.data;
};

// Корреляционный анализ
export interface GetCorrelationsParams {
  widgetId: string;
  primaryMetric: string;
  secondaryMetric: string;
  dateRange?: {
    start: string; // ISO date
    end: string; // ISO date
  };
  limit?: number;
}

export const getCorrelations = async (
  params: GetCorrelationsParams
): Promise<ICorrelationData> => {
  const { data } = await httpClient.get<ApiResponse<ICorrelationData>>(
    `${BASE_URL}/correlations`,
    { params }
  );

  return data.data;
};

const analyticsAnomaliesApi = {
  detectAnomalies,
  getAnomalyHistory,
  trainAnomalyModel,
  updateAnomalySettings,
  getAnomalyAlerts,
  acknowledgeAnomalyAlert,
  resolveAnomalyAlert,
  dismissAnomalyAlert,
  acknowledgeAllAlerts,
  resolveAllAlerts,
  getCorrelations,
};
export default analyticsAnomaliesApi;

